import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { OrganizationSelectors } from '@copilot/common/store/selectors/organization';
import isEmpty from 'lodash/isEmpty';
import { RootState } from '@copilot/common/store/reducers';
import { useLocation } from 'react-router-dom';

interface ClarityProps {
    // Clarity Project ID
    projectId: string;
    isEnabled: boolean;
    children: React.ReactNode;
}

/**
 * Clarity provider. It must be included before the main application.
 * @param props ClarityProps
 * @returns
 */
function ClarityProvider(props: ClarityProps): React.ReactElement {
    const { projectId, isEnabled } = props;
    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
    const activeOrganization = useSelector(OrganizationSelectors.getActiveOrganization);
    const activeImpersonatorId = useSelector((state: RootState) => state.app.impersonatorId);
    const canEnable = isEnabled && projectId;
    const location = useLocation();

    useEffect(() => {
        if (canEnable) {
            // Initialize Clarity
            window.clarity = function () {
                (window.clarity.q = window.clarity.q || []).push(arguments);
            };
            window.clarity('consent', 'granted');
            window.clarity('set', 'projectId', projectId);

            // Load Clarity script
            const script = document.createElement('script');
            script.async = true;
            script.src = 'https://www.clarity.ms/tag/' + projectId;
            document.head.appendChild(script);
        }
    }, [canEnable, projectId]);

    // Handle user identification
    useEffect(() => {
        if (canEnable && activeMember) {
            // Identify user in Clarity
            // Set user properties
            const userId = activeImpersonatorId ?? activeMember.userId;
            if (isEmpty(activeImpersonatorId)) {
                window.clarity('identify', userId, undefined, undefined, activeMember.email);
            } else {
                window.clarity('identify', userId);
                window.clarity('set', 'impersonatorId', activeImpersonatorId);
            }
            window.clarity('set', 'userId', userId);
            window.clarity('set', 'organizationId', activeMember.organizationId);
            window.clarity('set', 'orgMemberId', activeMember.id);
            window.clarity('set', 'userEmail', activeMember.email);
            window.clarity('set', 'userName', activeMember.name);
        }
    }, [
        activeMember?.userId,
        activeImpersonatorId,
        canEnable,
        activeMember,
        activeOrganization,
        location.pathname,
        location.search,
        location.hash,
    ]);

    return <>{props.children}</>;
}

export default ClarityProvider;
